// Live appointment list with export buttons.
// Sorted by date and time. The list panel sits below the matrix.

import type { Store } from '../store';
import type { Appointment } from '../types';
import { formatDateShort, formatTime } from '../time';
import { zoneForMinutes } from '../logic';
import { sortAppointments } from '../logic';
import { buildExport, type ExportFormat } from '../export';
import { el, clear } from './dom';

export function renderAppointmentList(store: Store): HTMLElement {
  const root = el('section', { class: 'appointment-list-panel', 'aria-label': 'Terminliste' });
  const heading = el('h2', { class: 'panel-heading' }, 'Termine');
  const counter = el('span', { class: 'panel-counter' }, '0');
  heading.append(' ', counter);

  const exportRow = el('div', { class: 'export-row', 'aria-label': 'Export' });
  const exportButtons = new Map<ExportFormat, HTMLButtonElement>();
  for (const fmt of ['json', 'csv', 'ics'] as ExportFormat[]) {
    const btn = el(
      'button',
      { type: 'button', class: 'export-btn' },
      fmt.toUpperCase(),
    ) as HTMLButtonElement;
    btn.addEventListener('click', () => {
      const s = store.get();
      if (s.appointments.length === 0) return;
      const payload = buildExport(fmt, s.config, s.appointments);
      triggerDownload(payload.filename, payload.mime, payload.content);
    });
    exportButtons.set(fmt, btn);
    exportRow.append(btn);
  }

  const header = el('header', { class: 'list-header' }, heading, exportRow);

  const list = el('ul', { class: 'appointment-list' });
  const emptyHint = el(
    'p',
    { class: 'appointment-empty' },
    'Noch keine Termine. Klicke in die Matrix oder ziehe mit gedrückter Maustaste, um mehrere zu erstellen.',
  );

  root.append(header, list, emptyHint);

  store.subscribe((s) => {
    const sorted = sortAppointments(s.appointments);
    counter.textContent = String(sorted.length);
    clear(list);
    emptyHint.style.display = sorted.length === 0 ? 'block' : 'none';
    for (const a of sorted) list.append(renderItem(store, a));
    for (const btn of exportButtons.values()) btn.disabled = sorted.length === 0;
  });

  return root;
}

function renderItem(store: Store, a: Appointment): HTMLElement {
  const zone = zoneForMinutes(a.startMinutes);
  const item = el(
    'li',
    { class: `appointment-item zone-${zone}` },
    el('span', { class: 'appointment-date' }, formatDateShort(a.date)),
    el('span', { class: 'appointment-time' }, `${formatTime(a.startMinutes)}–${formatTime(a.endMinutes)}`),
  );
  const remove = el('button', {
    type: 'button',
    class: 'appointment-remove',
    'aria-label': 'Termin entfernen',
    title: 'Entfernen',
  }, '×');
  remove.addEventListener('click', () => store.removeAppointment(a.id));
  item.append(remove);
  return item;
}

function triggerDownload(filename: string, mime: string, content: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
